/**
 * Okta OIDC client + auth React context/hook, split out from `auth.tsx` so the
 * component module exports only `<AuthProvider>` (keeps React Fast Refresh
 * working). See `auth.tsx` for the architecture notes; the `userManager`
 * singleton, the auth context, and the `useAuth` consumer hook live here
 * because none of them are React components.
 */

import { createContext, useContext } from "react";
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

const OKTA_ISSUER = import.meta.env.VITE_OKTA_ISSUER;
const OKTA_CLIENT_ID = import.meta.env.VITE_OKTA_CLIENT_ID;

// Fail loudly at module load if the build didn't get an Okta config, rather
// than redirecting to a malformed authorize URL. Matches the fail-fast guard
// the infra uses for the same values (infra/bin/app.ts).
if (!OKTA_ISSUER || !OKTA_CLIENT_ID) {
  throw new Error(
    "Okta OIDC not configured: VITE_OKTA_ISSUER and VITE_OKTA_CLIENT_ID must " +
      "be set. See apps/frontend/.env.development (preview) and .env.production.",
  );
}

// Build the redirect URIs from the live origin so the same code works on
// CloudFront (https://*.cloudfront.net), localhost dev (http://localhost:5173),
// and any future custom domain — as long as each origin is added to the
// Okta app's Sign-in / Sign-out redirect URIs list.
function origin(): string {
  return window.location.origin;
}

export const userManager = new UserManager({
  authority: OKTA_ISSUER,
  client_id: OKTA_CLIENT_ID,
  redirect_uri: `${origin()}/auth/callback`,
  post_logout_redirect_uri: `${origin()}/`,
  response_type: "code",
  // `groups` makes Okta add the groups claim to the access token (the claim
  // is scoped to it on the authorization server); authz.ts reads it for RBAC.
  scope: "openid email profile groups",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Auto-refresh access tokens before they expire so the user doesn't get
  // logged out mid-session.
  automaticSilentRenew: true,
  // We intentionally skip session monitoring — Okta's hidden iframe checks
  // don't play nicely with strict cookie policies and aren't necessary for
  // a long-lived dashboard tab.
  monitorSession: false,
  loadUserInfo: true,
});

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  /** Display name preference: profile.name → email → preferred_username → sub. */
  displayName: string | null;
  email: string | null;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
